<template>
  <div class="container">
    <DataGetter
      :url="`${apiRoute}/dashboard`"
      :params="{
        region: selectedRegion,
        /*publication,*/
        product: 'Renewable heat',
      }"
      :initialData="
        [
          'Share of renewables in heat consumption',
          'Share of electricity in heat consumption',
        ].reduce(
          (flows, flow) => [
            ...flows,
            { flow: `${flow}, Industry`, value: 0, year: 2022 },
            { flow: `${flow}, Industry`, value: 0, year: 2028 },
            { flow: `${flow}, Buildings`, value: 0, year: 2022 },
            { flow: `${flow}, Buildings`, value: 0, year: 2028 },
          ],
          []
        )
      "
      v-slot="{ data }"
      class="m-block m-block--charts m-block--charts-half indicators-wrapper"
    >
      <IndicatorBox
        :title="`Industry heat indicators, ${selectedRegion}`"
        :indicators="
          data
            .filter((row) => row.flow.includes('Industry'))
            .sort((a, b) => {
              const flowNameA = a.flow.replace(', Industry', '');
              const flowNameB = b.flow.replace(', Industry', '');
              return flowNameA === flowNameB
                ? a.year - b.year
                : indicatorFlows.indexOf(flowNameA) -
                    indicatorFlows.indexOf(flowNameB);
            })
            .map((row) => ({
              title: `${row.flow.replace(', Industry', '')}, ${row.year}`,
              number: parseInt(row.value),
              units: '%',
              description: row.year === 2028 ? 'Forecast' : null,
            }))
        "
        compact
      />
      <IndicatorBox
        :title="`Buildings heat indicators, ${selectedRegion}`"
        :indicators="
          data
            .filter((row) => row.flow.includes('Buildings'))
            .sort((a, b) => {
              const flowNameA = a.flow.replace(', Buildings', '');
              const flowNameB = b.flow.replace(', Buildings', '');
              return flowNameA === flowNameB
                ? a.year - b.year
                : indicatorFlows.indexOf(flowNameA) -
                    indicatorFlows.indexOf(flowNameB);
            })
            .map((row) => ({
              title: `${row.flow.replace(', Buildings', '')}, ${row.year}`,
              number: parseFloat(parseFloat(row.value).toFixed(0)),
              units: '%',
              description: row.year === 2028 ? 'Forecast' : null,
            }))
        "
        :notes="countryNotes[selectedRegion]"
        compact
      >
      </IndicatorBox>
    </DataGetter>
    <ControlGroup>
      <DataGetter
        :url="`${endpoint}/list/sector`"
        v-slot="{ data }"
        :initialData="[]"
        :childElementType="null"
      >
        <ButtonGroup
          label="Sector"
          :options="data.map((value) => ({ label: value, value }))"
          v-model="selectedSector"
        />
      </DataGetter>
    </ControlGroup>
    <DataGetter
      :url="endpoint"
      :params="{
        region: selectedRegion,
        publication,
        sector: selectedSector,
        product: [
          ...heatProducts,
          'Share of modern renewables in heat consumption',
        ],
      }"
      :childElementType="null"
      :initialData="[]"
      v-slot="{ data }"
    >
      <DataSlicer
        :data="data"
        xAxisDimension="year"
        yAxisDimension="product"
        valueDimension="value"
        v-slot="{ slicedData }"
        class="m-block m-block--charts m-block--charts-half"
      >
        <FiveYearChart
          :title="`5-year consumption growth, ${selectedSector}`"
          :selectedRegion="selectedRegion"
          :products="heatProducts"
          productLabel="Heating technology"
          :periods="[
            [2017, 2022],
            [2023, 2028],
          ]"
          showAll
          computeGrowth
          :data="data"
        />
        <ChartLayout
          :title="`Renewable heat consumption, ${selectedSector.toLowerCase()}, ${selectedRegion}`"
          units="PJ"
          half
        >
          <Chart
            key="flowByYear"
            :options="
              buildOptions(
                {
                  chart: {
                    height: 480,
                  },
                  series: (slicedData || [])
                    .map((series) =>
                      series.name ===
                      'Share of modern renewables in heat consumption'
                        ? {
                            ...series,
                            type: 'line',
                            colorIndex: 8,
                            yAxis: 1,
                            connectNulls: true,
                            marker: { enabled: true },
                            tooltip: {
                              valueSuffix: '%',
                            },
                            zIndex: 99,
                          }
                        : {
                            ...series,
                            colorIndex: heatProducts.indexOf(series.name),
                          }
                    )
                    .toSorted(
                      (a, b) =>
                        heatProducts.indexOf(a.name) -
                        heatProducts.indexOf(b.name)
                    ),
                  yAxis: [
                    {
                      title: { text: 'Total consumption' },
                      reversedStacks: false,
                      plotLines: [
                        {
                          value: 0,
                          className: 'highcharts-zero-axis',
                          zIndex: 99,
                        },
                      ],
                    },
                    {
                      title: { text: 'Renewable share' },
                      opposite: true,
                      min: 0,
                      labels: {
                        format: '{value}%',
                      },
                    },
                  ],
                  xAxis: {
                    plotBands: [
                      {
                        from: projectionYear - 0.5,
                        to: 2050,
                        label: {
                          text: 'Forecast',
                        },
                      },
                    ],
                  },
                  tooltip: {
                    valueSuffix: ' PJ',
                  },
                  legend: {
                    enabled: true,
                    align: 'right',
                    verticalAlign: 'middle',
                    layout: 'vertical',
                    width: '20%',
                    floating: false,
                    reversed: true,
                  },
                },
                'column'
              )
            "
            :updateArgs="[true, true, true]"
          />
          <template #notes v-if="noteBySector[selectedSector]">
            <p>
              {{ noteBySector[selectedSector] }}
            </p>
          </template>
        </ChartLayout>
      </DataSlicer>
    </DataGetter>
    <TextContent
      product="Renewable heat"
      :region="selectedRegion"
      :apiRoute="apiRoute"
      :publication="publication"
    />
  </div>
</template>

<script>
import {
  DataGetter,
  DataSlicer,
  ControlGroup,
  ButtonGroup,
  IndicatorBox,
} from "@international-energy-agency/vue-ui";
import ChartOptions, {
  ChartLayout,
} from "@international-energy-agency/vue-ui/highcharts";
import { Chart } from "highcharts-vue";
import TextContent from "./TextContent.vue";
import FiveYearChart from "./FiveYearChart.vue";
export default {
  components: {
    DataGetter,
    DataSlicer,
    ControlGroup,
    ButtonGroup,
    ChartLayout,
    Chart,
    IndicatorBox,
    FiveYearChart,
    TextContent,
  },
  data() {
    return {
      projectionYear: 2023,
      selectedSector: "All sectors",
      selectedProduct: "Renewable electricity used for heat",
      indicatorFlows: [
        "Share of renewables in heat consumption",
        "Share of electricity in heat consumption",
      ],
      noteBySector: {
        "All sectors":
          "In this report, heat consumption in the buildings sector covers space heating, water heating and cooking applications. Ambient heat from heat pumps used in the industry sector is not accounted for, due to limited data availability.",
        Buildings:
          "In this report, heat consumption in the buildings sector covers space heating, water heating and cooking applications.",
        Industry:
          "Ambient heat from heat pumps used in the industry sector is not accounted for, due to limited data availability.",
      },
    };
  },
  props: {
    apiRoute: {
      type: String,
      default: "https://api.iea.org/renewables",
    },
    publication: {
      type: String,
      default: "2023",
    },
    selectedRegion: {
      type: String,
      required: true,
    },
    countryNotes: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    endpoint() {
      const { apiRoute } = this;
      return `${apiRoute}/heat`;
    },
    heatProducts() {
      const { selectedSector } = this;
      return [
        selectedSector === "Industry" ? "" : "Ambient heat use from heat pumps",
        "Geothermal heat (direct use)",
        "Modern bioenergy",
        "Renewable district heat",
        "Renewable electricity used for heat",
        "Solar thermal heat",
      ];
    },
  },
  methods: {
    buildOptions: ChartOptions.buildOptions,
    getMax(data, years) {
      if (!data || !years) return undefined;
      const { selectedFlow } = this;
      const hasAccelerated = data.some(
        (r) => r.flow === selectedFlow && r.case === "ACC"
      );
      const filteredData = data.filter(
        (r) =>
          r.flow === selectedFlow &&
          r.case === (hasAccelerated ? "ACC" : "MAIN")
      );
      return years.reduce((max, year) => {
        const yearSum = filteredData
          .filter((r) => r.year === year)
          .reduce((sum, r) => sum + r.value, 0);
        return yearSum > max ? yearSum : max;
      }, 0);
    },
  },
};
</script>

<style scoped>
.case-select {
  flex: unset !important;
}
.indicators-wrapper {
  padding-bottom: 0px;
}
</style>
